<Container width={shapeWidth} height={shapeHeight} minWidth={shapeWidth} minHeight={shapeHeight}>
  <Graphics width={shapeWidth} height={shapeHeight} draw={drawShape} />
</Container>

<script>
import { computed } from "canvasengine";
import { resolveDynamicValue } from "./parse-value";
import { getShapeBox, translatePolygonPoints } from "./shape-utils";

const props = defineProps();
const { object } = props;
const sprite = object();

const read = (prop, fallback) => prop ? prop() : fallback;

const toNumber = (value, fallback = 0) => {
  const resolved = resolveDynamicValue(value, sprite);
  const num = typeof resolved === 'number' ? resolved : parseFloat(resolved);
  return Number.isFinite(num) ? num : fallback;
};

const toColor = (value, fallback) => {
  const resolved = resolveDynamicValue(value, sprite);
  if (typeof resolved === 'number') return resolved;
  if (typeof resolved === 'string' && resolved.startsWith('#')) {
    return parseInt(resolved.slice(1), 16);
  }
  return resolved ?? fallback;
};

const config = computed(() => ({
  type: read(props.type, 'rectangle'),
  fill: toColor(read(props.fill, '#ffffff'), 0xffffff),
  radius: toNumber(read(props.radius, 8), 8),
  width: toNumber(read(props.width, 16), 16),
  height: toNumber(read(props.height, 16), 16),
  x1: toNumber(read(props.x1, 0), 0),
  y1: toNumber(read(props.y1, 0), 0),
  x2: toNumber(read(props.x2, 16), 16),
  y2: toNumber(read(props.y2, 0), 0),
  opacity: Math.max(0, Math.min(1, toNumber(read(props.opacity, 1), 1))),
  points: read(props.points, []),
  line: read(props.line, null)
}));

const shapeBox = computed(() => getShapeBox(config(), toNumber));

const shapeWidth = computed(() => shapeBox().width);
const shapeHeight = computed(() => shapeBox().height);

const drawShape = (g) => {
  const cfg = config();
  const box = shapeBox();

  if (cfg.type === 'circle') {
    g.circle(cfg.radius, cfg.radius, cfg.radius);
  } else if (cfg.type === 'ellipse') {
    g.ellipse(box.width / 2, box.height / 2, box.width / 2, box.height / 2);
  } else if (cfg.type === 'line') {
    g.moveTo(cfg.x1 + box.offsetX, cfg.y1 + box.offsetY);
    g.lineTo(cfg.x2 + box.offsetX, cfg.y2 + box.offsetY);
  } else if (cfg.type === 'polygon' && Array.isArray(cfg.points)) {
    g.poly(translatePolygonPoints(cfg.points, box, toNumber));
  } else if (cfg.type === 'rounded-rectangle') {
    g.roundRect(0, 0, box.width, box.height, toNumber(read(props.radius, 4), 4));
  } else {
    g.rect(0, 0, box.width, box.height);
  }

  if (cfg.type === 'line') {
    const line = cfg.line ?? {};
    g.stroke({
      color: toColor(line.color, cfg.fill),
      width: toNumber(line.width, 1),
      alpha: toNumber(line.alpha, cfg.opacity)
    });
    return;
  }

  g.fill({ color: cfg.fill, alpha: cfg.opacity });

  if (cfg.line) {
    g.stroke({
      color: toColor(cfg.line.color, cfg.fill),
      width: toNumber(cfg.line.width, 1),
      alpha: toNumber(cfg.line.alpha, cfg.opacity)
    });
  }
};
</script>
